import React, { useState, useEffect, useContext } from "react";
import JobGrid     from "../components/jobsList/JobGrid";
import JobGridSearch     from "../components/jobsList/JobGridSearch";
import { useFilters } from '@/context/FiltersContext';
import FilterBar from '../components/jobsList/filterBar/FilterBar';
import PremiumModal from '../components/premium/PremiumModal';
import algoliasearch from 'algoliasearch/lite';
import SearchBar     from "../components/jobsList/SearchBar";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";

const searchClient = algoliasearch(process.env.REACT_APP_ALGOLIA_APP_ID, process.env.REACT_APP_ALGOLIA_SEARCH_API_KEY);
const index = searchClient.initIndex('jobs');

export default function JobsList({ onSend }) {
  const {
    filters,
    setFilters,
    selectedFilters,
    setSelectedFilters
  }                                             = useFilters();
  const { hash }                                = useLocation();
  const [hashView, hashSearch] = hash.slice(1).split('_')

  const [activeFilter, setActiveFilter]         = useState('categories');
  const [displayedView, setDisplayedView]       = useState(hashView || 'filters');
  const [algoliaFilters, setAlgoliaFilters]     = useState('');
  const [jobCount, setJobCount]                 = useState(0);
  const [jobs, setJobs]                         = useState([]);
  const [jobsSearch, setJobsSearch]             = useState([]);
  const [search, setSearch]                     = useState(hashSearch || '');
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumModalReason, setPremiumModalReason] = useState('');
  const { user, updateUser }                    = useContext(AuthContext);

  const openPremiumModal = (reason) => {
    setPremiumModalReason(reason);
    setShowPremiumModal(true);
  };

  const handleCloseSearch = () => {
    toggleDisplayView('filters')
    setSearch('')
  }

  const handleSearch = (e) => {
    if ( e.target.value == '' ) {
      toggleDisplayView('filters')
    } else {
      toggleDisplayView('jobsSearch')
    }
    setSearch(e.target.value)
  };

  async function fetchJobsWithSearch() {
    const {hits} = await index.search(search, {
      hitsPerPage: 1000
    })
    setJobsSearch(setJobsWithFilters(hits, true));
  }

  async function fetchJobsWithFacets() {
    if ( !algoliaFilters ) return null
    const fullFilter = algoliaFilters.filter(Boolean).join(' AND ')
    const otherOnlyFilter = algoliaFilters[1]
    const [jobRes, categoryFacetRes] = await Promise.all([
      index.search('', {
        filters: fullFilter,
        facets: ['qualities', 'interests', 'study_levels'],
        hitsPerPage: 1000
      }),
      index.search('', {
        filters: otherOnlyFilter,
        facets: ['categories'],
        hitsPerPage: 0
      })
    ]);

    setJobCount(jobRes.hits.length);
    setJobs(setJobsWithFilters(jobRes.hits));

    const updatedFilters = { ...filters };
    if (Object.keys(updatedFilters).length === 0) return null

    const updateFacetCounts = (facetKey) => {
      updatedFilters[facetKey] = updatedFilters[facetKey].map(item => ({
        ...item,
        jobCount: jobRes.facets?.[facetKey]?.[item.id.toString()] || 0
      }));
    };
    const updateCategorieFacetCounts = (facetKey) => {
      updatedFilters[facetKey] = updatedFilters[facetKey].map(item => ({
        ...item,
        jobCount: categoryFacetRes.facets?.[facetKey]?.[item.id.toString()] || 0
      }));
    };

    ['qualities', 'interests', 'study_levels'].forEach(updateFacetCounts);
    ['categories'].forEach(updateCategorieFacetCounts);

    setFilters(updatedFilters);
  }

  function buildAlgoliaFilterString() {
    const categoryFilters = selectedFilters.categories
      .map(({id}) => `categories:${id}`)
      .join(' OR ');

    const studyLevelsFilters = selectedFilters.study_levels
      .map(({id}) => `study_levels:${id}`)
      .join(' OR ');

    const otherFilters = [
      ...(selectedFilters.qualities || []).map(({id}) => `qualities:${id}`),
      ...(selectedFilters.interests || []).map(({id}) => `interests:${id}`)
    ];
  
    const otherFilterString = otherFilters.length > 0
      ? `(${otherFilters.join(' AND ')})`
      : '';

    setAlgoliaFilters([categoryFilters, otherFilterString, studyLevelsFilters]);
  }

  function setJobsWithFilters(algoliaJobs, all = false) {
    // Transforme les selectedFilters en objets { id: label } pour chaque groupe
    let filtersToMap = { ...selectedFilters }
    if ( all ) {
      filtersToMap = { ...filters }
    }
    if ( !filters || filters.length === 0 ) return []
    const filterMaps = {};
    ['categories', 'qualities', 'interests', 'study_levels'].forEach(group => {
      filterMaps[group] = Object.fromEntries(
        filtersToMap[group].map(({ id, label }) => [parseInt(id), label])
      );
    });

    // Mappe chaque job en filtrant & renommant ses champs
    const displayableJobs = algoliaJobs.map(job => {
      const mappedJob = { ...job };

      // Pour chaque type de filtre, on garde seulement les IDs présents dans selectedFilters
      ['categories', 'qualities', 'interests', 'study_levels'].forEach(group => {
        if (Array.isArray(job[group])) {
          mappedJob[group] = job[group]
            .filter(id => filterMaps[group][id])            // Ne garder que les IDs sélectionnés
            .map(id => filterMaps[group][id]);              // Remplacer ID par label
        }
      });

      return mappedJob;
    });

    return displayableJobs;
  }

  const handleToggleItem = (group, item) => {
    setSelectedFilters((prev) => {
      const exists = prev[group].some((i) => i.id === item.id);
      const updatedGroup = exists
        ? prev[group].filter((i) => i.id !== item.id)
        : [...prev[group], item];
      return { ...prev, [group]: updatedGroup };
    });
  };

  useEffect(() => {
    if ( search !== '' ) {
      fetchJobsWithSearch()
      window.location.hash = `jobsSearch_${search}`
    } else {
      window.location.hash = hashView
    }
  }, [search]);

  useEffect(() => {
    fetchJobsWithFacets()
  }, [algoliaFilters]);

  useEffect(() => {
    if ( search !== '' && filters && filters.length !== 0 )
      fetchJobsWithSearch()
    if ( jobs.length === 0 && displayedView !== 'jobsSearch' && filters )
      buildAlgoliaFilterString()
  }, [filters]); 
    
  useEffect(() => {
    buildAlgoliaFilterString()
  }, [selectedFilters]);

  useEffect(() => {
    if ( hashView && hashView !== 'jobsSearch' ) {
      setDisplayedView(hashView)
      setSearch('')
    }
    if ( !hashView ){
      setDisplayedView('filters')
      setSearch('')
    }
  }, [hash]);
  
  const handleClearAll = () => {
    setSelectedFilters({ categories: [], qualities: [], interests: [], study_levels: [] });
  };

  const handleClearFilter = (filter) => {
    setSelectedFilters((prev) => {
      return { ...prev, [filter]: [] };
    })
  };

  const toggleDisplayView = (view) => {
    if ( view !== 'jobsSearch')
      window.location.hash = view
    setDisplayedView(view)
  }


  return (
    <div className="relative flex flex-1 flex-col h-full w-full px-4 pt-4 items-center">
      {(displayedView === 'filters' || displayedView === 'jobsSearch') &&
        <SearchBar
          value={search}
          onChange={handleSearch}
          onClose={handleCloseSearch}
        />
      }
      {displayedView === 'filters' &&
        <FilterBar
          filters={filters}
          jobCount={jobCount}
          activeFilter={activeFilter}
          setActiveFilter={setActiveFilter}
          selectedFilters={selectedFilters}
          onToggleItem={handleToggleItem}
          onToggleDisplayView={toggleDisplayView}
          onClearFilter={handleClearFilter}
          onClearAll={handleClearAll}
        />
      }
      {displayedView === 'jobs' &&
        <JobGrid
          jobs={jobs}
          onToggleDisplayView={toggleDisplayView}
          jobCount={jobCount}
          selectedFilters={selectedFilters}
          onSend={onSend}
          onOpenPremiumModal={openPremiumModal}
        />
      }
      {displayedView === 'jobsSearch' &&
        <JobGridSearch
          jobs={jobsSearch}
          onSend={onSend}
          onOpenPremiumModal={openPremiumModal}
        />
      }

      <PremiumModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        askedForPremium={user?.asked_for_premium}
        onUpdateUser={updateUser}
        context="Tu as déjà rencontré 2 professionnels"
        reason={premiumModalReason}
      />
    </div>
  );
}
